import { useMutation, useQuery } from '@tanstack/react-query'

import { apiClient } from '@/lib/apiClient'
import { accessToken } from '@/lib/staffSession'

export interface FunnelStep {
  key: string
  count: number
  /** Part de l'étape précédente. `null` pour la première. */
  conversion: number | null
}

export interface Overview {
  event_name: string
  currency: string
  generated_at: string
  orders_total: number
  orders_paid: number
  orders_pending: number
  orders_cancelled: number
  tickets_issued: number
  attendees_expected: number
  attendees_checked_in: number
  food_items_sold: number
  tables_reserved: number
  votes_cast: number
  candidates_approved: number
  funnel: FunnelStep[]
}

export interface RevenueLine {
  item_type: string
  quantity: number
  gross_minor: number
  discount_minor: number
  net_minor: number
}

export interface RevenueReport {
  currency: string
  collected_minor: number
  refunded_minor: number
  net_minor: number
  outstanding_minor: number
  average_order_minor: number
  paid_orders: number
  by_type: RevenueLine[]
  by_payment_method: Record<string, number>
}

export interface SalesTimeline {
  currency: string
  points: { day: string; orders: number; net_minor: number }[]
  top_items: {
    ref_id: string | null
    item_type: string
    label: string
    quantity: number
    net_minor: number
  }[]
}

export interface ExportKind {
  key: string
  label: string
  description: string
  /** L'UI le signale avant le clic. */
  personal_data: boolean
}

function options(eventId: string | undefined, token: string) {
  return { token, ...(eventId ? { eventId } : {}) }
}

export function useOverview(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['analytics-overview', eventId],
    queryFn: async () =>
      apiClient.get<Overview>(
        `/admin/events/${eventId}/analytics/overview`,
        options(eventId, await accessToken()),
      ),
    enabled: Boolean(eventId) && enabled,
  })
}

/**
 * Chiffre d'affaires.
 *
 * Derrière un droit distinct des volumes : un 403 ici n'est pas une panne,
 * c'est une réponse. La page masque simplement le bloc.
 */
export function useRevenue(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['analytics-revenue', eventId],
    queryFn: async () =>
      apiClient.get<RevenueReport>(
        `/admin/events/${eventId}/analytics/revenue`,
        options(eventId, await accessToken()),
      ),
    enabled: Boolean(eventId) && enabled,
    retry: false,
  })
}

export function useTimeline(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['analytics-timeline', eventId],
    queryFn: async () =>
      apiClient.get<SalesTimeline>(
        `/admin/events/${eventId}/analytics/timeline`,
        options(eventId, await accessToken()),
      ),
    enabled: Boolean(eventId) && enabled,
    retry: false,
  })
}

export function useExportCatalogue(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['export-catalogue', eventId],
    queryFn: async () =>
      apiClient.get<{ exports: ExportKind[] }>(
        `/admin/events/${eventId}/exports`,
        options(eventId, await accessToken()),
      ),
    enabled: Boolean(eventId) && enabled,
    retry: false,
  })
}

/**
 * Génère et télécharge un export.
 *
 * On passe par `fetch` plutôt que par le client typé : la réponse est un
 * fichier, pas du JSON. Le nom vient du serveur (`Content-Disposition`) —
 * le front n'invente pas de convention de nommage de son côté.
 */
export function useDownloadExport(eventId: string | undefined) {
  return useMutation({
    mutationFn: async ({ key, reason }: { key: string; reason?: string }) => {
      const prefix = import.meta.env.VITE_API_PREFIX ?? '/api/v1'
      const response = await fetch(`${prefix}/admin/events/${eventId}/exports/${key}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await accessToken()}`,
          ...(eventId ? { 'X-Event-Id': eventId } : {}),
        },
        body: JSON.stringify({ reason: reason ?? null }),
      })
      if (!response.ok) throw new Error(String(response.status))

      const disposition = response.headers.get('Content-Disposition') ?? ''
      const match = /filename="([^"]+)"/.exec(disposition)
      const blob = await response.blob()

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = match?.[1] ?? `${key}.csv`
      link.click()
      URL.revokeObjectURL(url)
      return link.download
    },
  })
}
